import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight, ArrowRight, Eye } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/** Carousel of portfolio samples. Used on the Home page (`compact`) and as
 * the standalone Portfolio page hero (`expanded`). Pulls the live list from
 * /api/portfolio/items so adding a PDF on the backend updates both views.
 */
export default function PortfolioSlider({ variant = "compact" }) {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const auto = useRef(null);
    const expanded = variant === "expanded";

    useEffect(() => {
        api.get("/portfolio/items").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, []);

    useEffect(() => {
        if (!items.length) return undefined;
        clearTimeout(auto.current);
        auto.current = setTimeout(() => setIdx((i) => (i + 1) % items.length), 6000);
        return () => clearTimeout(auto.current);
    }, [idx, items.length]);

    if (!items.length) return null;
    const cur = items[idx];

    const goto = (delta) => setIdx((i) => (i + delta + items.length) % items.length);

    return (
        <div className="relative w-full" data-testid={`portfolio-slider-${variant}`}>
            <div className={`grid lg:grid-cols-5 gap-6 lg:gap-8 ${expanded ? "" : "items-center"}`}>
                {/* PDF preview pane */}
                <div className={`lg:col-span-3 relative tech-card overflow-hidden ${expanded ? "min-h-[640px]" : "min-h-[420px]"} bg-slate-950`}>
                    <iframe
                        key={cur.slug}
                        src={`${cur.stream_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        title={cur.title}
                        className="w-full h-full absolute inset-0 border-0 select-none"
                        loading="lazy"
                        // Lock iframe to our own origin; disables download triggers via inline links.
                        sandbox="allow-same-origin allow-scripts"
                        data-testid={`portfolio-pdf-${cur.slug}`}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ pointerEvents: "auto" }}
                    />
                    {/* Top-right "preview only" pill — discourages download intent */}
                    <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-[10px] font-mono-alt text-cyan-300 z-10 pointer-events-none">
                        PREVIEW ONLY · {idx + 1}/{items.length}
                    </div>
                    {/* Bottom gradient to mask the PDF page-fold and overlay slider controls */}
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
                </div>

                {/* Meta + CTA pane */}
                <div className="lg:col-span-2 space-y-5">
                    <div
                        className="px-3 py-1.5 inline-flex items-center gap-2 rounded-full border text-[10px] uppercase tracking-[0.18em] font-mono-alt"
                        style={{
                            color: cur.accent,
                            borderColor: `${cur.accent}55`,
                            background: `${cur.accent}11`,
                        }}
                        data-testid="portfolio-category"
                    >
                        {cur.category}
                    </div>
                    <h3 className={`font-heading font-extrabold tracking-tight text-slate-50 ${expanded ? "text-4xl sm:text-5xl" : "text-3xl"}`}>
                        {cur.title}
                    </h3>
                    <p className="text-slate-300 leading-relaxed text-[15px]">
                        {cur.summary}
                    </p>

                    <div className="flex flex-wrap gap-3 pt-2">
                        <Link to="/get-started" className="btn-primary" data-testid="portfolio-cta-quote">
                            Request a similar drawing <ArrowRight size={16} weight="bold" />
                        </Link>
                        {!expanded && (
                            <Link to="/portfolio" className="btn-ghost" data-testid="portfolio-cta-view-all">
                                <Eye size={16} /> View full portfolio
                            </Link>
                        )}
                    </div>

                    {/* Thumbnail nav */}
                    <div className="pt-4 border-t border-slate-800/80">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-mono-alt text-slate-500 mb-3">Browse samples</div>
                        <div className="flex flex-wrap gap-2">
                            {items.map((it, i) => (
                                <button
                                    key={it.slug}
                                    onClick={() => setIdx(i)}
                                    className={`px-3 py-1.5 rounded-lg text-[11px] font-mono-alt border transition-colors ${
                                        i === idx
                                            ? "bg-cyan-500/15 border-cyan-400/60 text-cyan-100"
                                            : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                                    }`}
                                    data-testid={`portfolio-thumb-${it.slug}`}
                                    style={i === idx ? { borderColor: `${it.accent}88` } : undefined}
                                >
                                    {it.title.replace(/ \d+$/, "")}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Prev / Next arrows */}
            {items.length > 1 && (
                <>
                    <button
                        onClick={() => goto(-1)}
                        className="absolute left-2 lg:left-3 top-[260px] lg:top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-slate-900/90 border border-slate-700 text-slate-200 hover:bg-cyan-500/20 hover:border-cyan-400/60 flex items-center justify-center transition-colors"
                        aria-label="Previous"
                        data-testid="portfolio-prev"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                    <button
                        onClick={() => goto(1)}
                        className="absolute right-2 lg:right-[calc(40%+12px)] top-[260px] lg:top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-slate-900/90 border border-slate-700 text-slate-200 hover:bg-cyan-500/20 hover:border-cyan-400/60 flex items-center justify-center transition-colors"
                        aria-label="Next"
                        data-testid="portfolio-next"
                    >
                        <CaretRight size={20} weight="bold" />
                    </button>
                </>
            )}
        </div>
    );
}
